import React, { useState, useEffect, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, ToggleLeft, ToggleRight, Plus, X, BadgeCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import adminService from '../services/adminService';
import { formatDate } from '../utils/formatters';

const TABS = [
  { key: '', label: 'All Users' },
  { key: 'player', label: 'Players' },
  { key: 'owner', label: 'Venue Owners' },
  { key: 'admin', label: 'Admins' },
];

const ROLE_BADGES = {
  player: 'admin-badge-info',
  owner: 'admin-badge-warning',
  admin: 'admin-badge-danger',
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [toggling, setToggling] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phoneNumber: '', password: '', confirmPassword: '', role: 'courdowner',
  });

  const resetForm = () => setForm({
    firstName: '', lastName: '', email: '', phoneNumber: '', password: '', confirmPassword: '', role: 'courdowner',
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getUsers(page, activeTab, '');
      setUsers(data.users || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, activeTab]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const handleToggleStatus = async (userId, currentStatus) => {
    setToggling(userId);
    try {
      await adminService.updateUserStatus(userId, !currentStatus);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, isActive: !currentStatus } : u))
      );
      toast.success(currentStatus
        ? 'User deactivated successfully'
        : 'User activated and notified by email');
    } catch (err) {
      toast.error('Failed to update user status');
    } finally {
      setToggling(null);
    }
  };

  const handleVerifyEmail = async (userId) => {
    setVerifying(userId);
    try {
      const result = await adminService.verifyUserEmail(userId);
      setUsers((prev) => prev.map((user) => user.id === userId
        ? {
            ...user,
            isEmailVerified: result.isEmailVerified,
            emailVerifiedAt: result.emailVerifiedAt,
          }
        : user));
      toast.success('Email verified and user notified');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to verify email');
    } finally {
      setVerifying(null);
    }
  };

  const filteredUsers = search
    ? users.filter(
        (u) =>
          u.name?.toLowerCase().includes(search.toLowerCase()) ||
          u.email?.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#1F2937]">User Management</h1>
        <p className="text-sm text-[#667085] mt-1">Manage all platform users</p>
      </div>

      <div className="admin-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex gap-1 bg-[#F9FAFB] p-1 rounded-lg">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={activeTab === tab.key ? 'admin-tab-active' : 'admin-tab-inactive'}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085]" size={18} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users..."
                className="admin-input pl-10 pr-4 py-2 w-full sm:w-64"
              />
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="admin-btn-primary inline-flex items-center gap-2 whitespace-nowrap"
            >
              <Plus size={18} />
              Create User
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-admin-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12 text-[#667085]">
            <Users className="mx-auto mb-2" size={40} />
            <p className="text-sm">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Email Verification</th>
                  <th>Joined Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2B2B2B]">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-[#F9FAFB]">
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-admin-100 rounded-full flex items-center justify-center text-sm font-medium text-admin-700">
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <span className="font-medium text-[#1F2937]">{user.name}</span>
                      </div>
                    </td>
                    <td className="text-[#667085]">{user.email}</td>
                    <td>
                      <span className={`admin-badge ${ROLE_BADGES[user.role] || 'admin-badge-neutral'}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <span className={user.isActive ? 'admin-badge-success' : 'admin-badge-danger'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {user.isEmailVerified && user.emailVerifiedAt ? (
                        <div>
                          <span className="admin-badge-success">Verified</span>
                          <p className="mt-1 text-xs text-[#667085]">{formatDate(user.emailVerifiedAt)}</p>
                        </div>
                      ) : (
                        <span className="admin-badge-warning">Unverified</span>
                      )}
                    </td>
                    <td className="text-[#667085]">{formatDate(user.createdAt)}</td>
                    <td>
                      <div className="flex flex-col items-start gap-2">
                        <button
                          onClick={() => handleToggleStatus(user.id, user.isActive)}
                          disabled={toggling === user.id}
                          className={`inline-flex items-center gap-1.5 text-sm font-medium ${
                            user.isActive ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'
                          } disabled:opacity-50`}
                        >
                          {toggling === user.id ? (
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : user.isActive ? (
                            <ToggleRight size={18} />
                          ) : (
                            <ToggleLeft size={18} />
                          )}
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        {(!user.isEmailVerified || !user.emailVerifiedAt) && (
                          <button
                            onClick={() => handleVerifyEmail(user.id)}
                            disabled={verifying === user.id}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-admin-700 hover:text-admin-800 disabled:opacity-50"
                          >
                            {verifying === user.id ? (
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <BadgeCheck size={18} />
                            )}
                            Verify Email
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-[#E4E7EC] mt-4">
            <p className="text-sm text-[#667085]">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="admin-btn-secondary"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="admin-btn-secondary"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl border border-[#E4E7EC] shadow-[0_4px_20px_rgba(0,0,0,.4)] w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-[#1F2937]">Create User</h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="text-[#667085] hover:text-[#667085]">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!form.firstName || !form.lastName || !form.email || !form.password) {
                toast.error('Please fill in all required fields');
                return;
              }
              if (form.password.length < 6) {
                toast.error('Password must be at least 6 characters');
                return;
              }
              if (form.password !== form.confirmPassword) {
                toast.error('Passwords do not match');
                return;
              }
              setCreating(true);
              try {
                const user = await adminService.createUser({
                  firstName: form.firstName,
                  lastName: form.lastName,
                  email: form.email,
                  phoneNumber: form.phoneNumber,
                  password: form.password,
                  role: form.role,
                });
                setUsers((prev) => [user, ...prev]);
                toast.success('User created successfully');
                setShowModal(false);
                resetForm();
              } catch (err) {
                toast.error(err.response?.data?.message || 'Failed to create user');
              } finally {
                setCreating(false);
              }
            }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="admin-label">First Name *</label>
                  <input type="text" value={form.firstName} onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))} className="admin-input" />
                </div>
                <div>
                  <label className="admin-label">Last Name *</label>
                  <input type="text" value={form.lastName} onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))} className="admin-input" />
                </div>
              </div>
              <div>
                <label className="admin-label">Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className="admin-input" />
              </div>
              <div>
                <label className="admin-label">Phone Number</label>
                <input type="tel" value={form.phoneNumber} onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))} className="admin-input" />
              </div>
              <div>
                <label className="admin-label">Role *</label>
                <select value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))} className="admin-input">
                  <option value="admin">Admin</option>
                  <option value="courdowner">Court Owner</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="admin-label">Password *</label>
                  <input type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} className="admin-input" />
                </div>
                <div>
                  <label className="admin-label">Confirm *</label>
                  <input type="password" value={form.confirmPassword} onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))} className="admin-input" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="admin-btn-secondary">Cancel</button>
                <button type="submit" disabled={creating} className="admin-btn-primary">
                  {creating ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Users({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
